<template>
  <v-card v-resize="onResize">
    <v-app-bar
      absolute
      color="white"
      elevate-on-scroll
      scroll-target="#scrolling-techniques-7"
    >
      <v-toolbar-title class="hidden-sm-and-down ml-5">
        Companies
      </v-toolbar-title>

      <v-progress-linear
        :active="loading"
        :indeterminate="loading"
        absolute
        bottom
        color="primary"
      ></v-progress-linear>

      <v-spacer></v-spacer>

      <v-tooltip left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn fab dark small v-bind="attrs" v-on="on" to="/employees">
            <v-icon>mdi-account-search</v-icon>
          </v-btn>
        </template>
        <span>Search Employees</span>
      </v-tooltip>

      <v-tooltip left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn fab dark small v-bind="attrs" v-on="on" @click="noEmployees()">
            <v-icon>mdi-printer</v-icon>
          </v-btn>
        </template>
        <span>No Employees</span>
      </v-tooltip>

      <v-spacer></v-spacer>

      <v-text-field
        v-model="search"
        label="Search Companies"
        prepend-icon="mdi-magnify"
        v-on:keyup="searchCompanies"
        clearable
        @click:clear="clearMessage"
        hide-details
        class="hidden-sm-and-down pl-4"
      />
    </v-app-bar>

    <v-sheet
      v-resize="onResize"
      :height="height"
      id="scrolling-techniques-7"
      class="overflow-y-auto"
    >
      <v-container style="margin-top: 70px; margin-bottom: 70px; padding: 0;">
        <v-card>
          <v-virtual-scroll
            :bench="benched"
            :items="showCompanies"
            :height="height - 80"
            item-height="64"
          >
            <template v-slot:default="{ item }">
              <v-list-item
                v-if="item.active === 'Yes'"
                :key="item.id"
                :to="`company/${item.id}`"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.name }}
                  </v-list-item-title>
                </v-list-item-content>

                <v-list-item-action>
                  <v-btn icon :to="`company/${item.id}`">
                    <v-icon color="primary">mdi-pencil</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>

              <v-list-item v-else :key="item.id">
                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.name }}
                  </v-list-item-title>
                </v-list-item-content>

                <v-list-item-action>
                  <v-btn
                    depressed
                    class="red lighten-1"
                    dark
                    @click="companyStatus(item.id, 'Yes')"
                  >
                    Inactive
                  </v-btn>
                </v-list-item-action>
              </v-list-item>

              <v-divider></v-divider>
            </template>
          </v-virtual-scroll>
        </v-card>
      </v-container>
    </v-sheet>

    <v-dialog v-model="showCompanyEdit" width="500">
      <v-card>
        <v-toolbar color="primary" dark>
          <v-btn icon dark @click="showCompanyEdit = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Edit Company</v-toolbar-title>
        </v-toolbar>

        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-text-field v-model="company.name" label="Name"></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="company.address"
                label="Address"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="company.city" label="City"></v-text-field>
            </v-col>
            <v-col cols="2">
              <v-text-field
                v-model="company.state"
                label="ST"
                v-mask="stateMask"
              ></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="company.zip"
                label="Zip"
                v-mask="zipMask"
              ></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="company.phone"
                label="Phone"
                v-mask="phoneMask"
              ></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="company.dueDate"
                label="Day Due"
                v-mask="numberMask"
              ></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="company.drawsYear"
                label="Draws per Yr"
                v-mask="numberMask"
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="company.ConsortiumId"
                :items="consortiaList"
                item-value="id"
                item-text="name"
                label="Consortium"
              ></v-select>
            </v-col>
            <v-col cols="12">
              <v-select
                v-model="company.TestTypeId"
                :items="testTypes"
                item-text="name"
                item-value="id"
                label="Program Type"
              ></v-select>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="company.percentDrug"
                label="Drug Draw Percent"
                v-mask="drawMask"
                suffix="%"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="company.percentAlcohol"
                label="Drug/Alcohol Percent"
                v-mask="drawMask"
                suffix="%"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="company.amountDrug"
                label="Drug Draw"
                v-mask="drawMask"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="company.amountAlcohol"
                label="Drug/Alcohol Draw"
                v-mask="drawMask"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="company.drugAlternate"
                label="Alt Drug Draw"
                v-mask="drawMask"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="company.alcoholAlternate"
                label="Alt Drug/Alcohol Draw"
                v-mask="drawMask"
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="company.active"
                :items="yesno"
                label="Active"
              ></v-select>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="addCompany()">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-btn
      class="mx-2"
      fab
      dark
      absolute
      right
      color="indigo"
      style="bottom: 10px;"
      @click="newCompany()"
    >
      <v-icon>mdi-plus</v-icon>
    </v-btn>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :multi-line="true"
      :timeout="6000"
    >
      {{ snackbar.message }}
      <v-btn dark text @click="snackbar.show = false">
        Close
      </v-btn>
    </v-snackbar>
  </v-card>
</template>

<script>
import _ from 'lodash'
import Api from '@/services/Api'
import XLSX from 'xlsx'
import { mask } from 'vue-the-mask'

export default {
  name: 'companies',

  directives: {
    mask,
  },

  data: () => ({
    benched: 0,
    companies: [],
    company: {},
    consortiaList: [],
    disable: true,
    drawMask: '###',
    height: 400,
    indeterminate: false,
    loading: true,
    logo: '',
    menu: false,
    numberMask: '##',
    numberSelected: 'Select All',
    phoneMask: '(###) ###-####',
    search: '',
    selectAll: false,
    selected: [],
    showCompanies: [],
    showCompanyEdit: false,
    snackbar: {
      show: false,
      color: '',
      message: '',
    },
    stateMask: 'AA',
    team: '',
    testTypes: [],
    yesno: ['Yes', 'No'],
    zipMask: '#####',
  }),

  created() {
    this.getCompanies()
    this.getConsortia()
    this.getTestTypes()
  },

  mounted() {
    this.onResize()
  },

  methods: {
    onResize() {
      this.height = window.innerHeight - 120
    },

    async getCompanies() {
      const clientId = this.$store.state.client.id

      try {
        const response = await Api().get('companies_all/' + clientId)

        const companies = response.data
        console.log(companies)

        this.companies = companies
        this.showCompanies = companies
        this.loading = false
      } catch (e) {
        console.error(e)
      }
    },

    async getConsortia() {
      const clientId = this.$store.state.client.id

      try {
        let consortiaList = {}
        consortiaList.id = '0'
        consortiaList.name = 'None'
        this.consortiaList.push(consortiaList)

        const response = await Api().get('just_consortia/' + clientId)

        for (let i in response.data) {
          consortiaList = {}
          consortiaList.id = response.data[i].id
          consortiaList.name = response.data[i].name
          this.consortiaList.push(consortiaList)
        }
      } catch (e) {
        console.error(e)
      }
    },

    async getTestTypes() {
      const clientId = this.$store.state.client.id

      try {
        let testTypes = {}
        testTypes.id = '0'
        testTypes.name = 'None'
        this.testTypes.push(testTypes)

        const response = await Api().get('test_types/' + clientId)

        for (let i in response.data) {
          testTypes = {}
          testTypes.id = response.data[i].id
          testTypes.name = response.data[i].name
          this.testTypes.push(testTypes)
        }
      } catch (e) {
        console.error(e)
      }
    },

    newCompany() {
      this.company.active = 'Yes'
      this.showCompanyEdit = true
    },

    async addCompany() {
      try {
        this.company.ClientId = this.$store.state.client.id

        const response = await Api().post('companies', this.company)

        this.companies.push(response.data.company)

        let test = this.companies.sort((a, b) => {
          let fa = a.name.toLowerCase(),
            fb = b.name.toLowerCase()

          if (fa < fb) {
            return -1
          }
          if (fa > fb) {
            return 1
          }

          return 0
        })

        this.companies = test

        this.showCompanyEdit = false

        this.snackbar.message = response.data.message
        this.snackbar.color = 'success'
        this.snackbar.show = true
      } catch (e) {
        this.snackbar.message = e.response.data.error
        this.snackbar.color = 'error'
        this.snackbar.show = true
      }
    },

    async noEmployees() {
      try {
        this.company.ClientId = this.$store.state.client.id

        const response = await Api().get(
          'companies_emp/' + this.$store.state.client.id
        )
        let companies = response.data

        let membersWS = XLSX.utils.json_to_sheet(companies)

        let wb = XLSX.utils.book_new()

        XLSX.utils.book_append_sheet(wb, membersWS, 'Members')

        XLSX.writeFile(wb, 'No_employees.csv')
        // XLSX.writeFile(test, this.companyName + '.xlsx')
      } catch (e) {
        this.snackbar.message = e.response.data.error
        this.snackbar.color = 'error'
        this.snackbar.show = true
      }
    },

    async companyStatus(companyId, active) {
      try {
        let formData = {}
        formData.active = active

        const response = await Api().put('companies/' + companyId, formData)

        let objIndex = this.companies.findIndex(obj => obj.id == companyId)

        this.companies[objIndex].active = active

        this.snackbar.message = response.data.message
        this.snackbar.color = 'success'
        this.snackbar.show = true
      } catch (e) {
        this.snackbar.message = e.response.data.error
        this.snackbar.color = 'error'
        this.snackbar.show = true
      }
    },

    searchCompanies() {
      this.loading = true

      if (this.search) {
        if (this.search.length > 3) {
          let search = this.search.toLowerCase()
          this.team = 'All'

          let companies = _.filter(this.companies, function(obj) {
            if (obj.name.toLowerCase().indexOf(search) !== -1) {
              return obj.name.toLowerCase().indexOf(search) !== -1
            }
          })

          this.showCompanies = companies
        }
      } else {
        this.showCompanies = this.companies
      }

      this.loading = false
    },

    clearMessage() {
      this.search = ''
      this.showCompanies = this.companies
    },
  },
}
</script>
